<script lang="ts" setup>
import { Form, FormField } from '@primevue/forms';
import { PasswordStrength } from '~/components/auth/labels/';
import { AppButton, AppPassword, AppTypography } from '~/components/base';
import useResetPassword from '~/composables/auth/forgot-password/useResetPassword';
import { EnterNewPassword as EnterNewPasswordContent } from '~/utils/contents/auth/forms/ForgotPasswordForm';

const { email, token } = defineProps<{
  email: string;
  token: string;
}>();

const emit = defineEmits<{
  success: [];
}>();

const { loading, form, errors, validations, resolver, submit } =
  useResetPassword();

const subtitleParts = computed(() => {
  const text = EnterNewPasswordContent.subtitle;
  const parts = text.split('<EMAIL>');
  return {
    before: parts[0] || '',
    after: parts[1] || '',
  };
});

const showPasswordStrength = computed((): boolean => {
  return !!form.value.password || !!form.value.password_confirmation;
});

const onFormSubmit = async ({ valid }: { valid: boolean }): Promise<void> => {
  if (!valid) return;

  const result = await submit(email, token);
  if (result) {
    emit('success');
  }
};

const canSubmit = computed((): boolean => {
  return validations.safeParse(form.value).success;
});
</script>

<template>
  <Form
    :resolver="resolver"
    data-testid="enter-new-password-form"
    class="enter-new-password-form flex w-full flex-col"
    @submit="onFormSubmit"
  >
    <div class="enter-new-password-form__header mb-8 flex flex-col text-center">
      <AppTypography type="title-4" variant="h4" class="mb-2">{{
        EnterNewPasswordContent.title
      }}</AppTypography>
      <AppTypography type="body">
        {{ subtitleParts.before }}<strong>{{ email }}</strong
        >{{ subtitleParts.after }}
      </AppTypography>
    </div>

    <FormField v-slot="$field" name="password" class="mb-3">
      <AppPassword
        v-model="form.password"
        required
        :feedback="false"
        :label="EnterNewPasswordContent.fields.password.label"
        :placeholder="EnterNewPasswordContent.fields.password.placeholder"
        :data-testid="EnterNewPasswordContent.fields.password.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid || !!errors.password?.length"
        :error-message="$field.error?.message || errors.password?.[0]"
        class="w-full"
        autocomplete="new-password"
      />
    </FormField>
    <FormField v-slot="$field" name="password_confirmation" class="mb-5">
      <AppPassword
        v-model="form.password_confirmation"
        required
        :feedback="false"
        :label="EnterNewPasswordContent.fields.password_confirmation.label"
        :placeholder="
          EnterNewPasswordContent.fields.password_confirmation.placeholder
        "
        :data-testid="
          EnterNewPasswordContent.fields.password_confirmation.dataTestId
        "
        :disabled="loading"
        :invalid="$field?.invalid || !!errors.password_confirmation?.length"
        :error-message="
          $field.error?.message || errors.password_confirmation?.[0]
        "
        class="w-full"
        autocomplete="new-password"
      />
    </FormField>

    <PasswordStrength
      v-if="showPasswordStrength"
      :password="form.password"
      class="mb-5"
      data-testid="password-strength"
    />

    <AppButton
      type="submit"
      :data-testid="EnterNewPasswordContent.actions.submitButton.dataTestId"
      :disabled="loading || !canSubmit"
      :loading="loading"
      :label="EnterNewPasswordContent.actions.submitButton.label"
    />
  </Form>
</template>

<style></style>
